import { Component, Input, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { DataService } from 'src/app/data.service';

/**
 *  ADMIN › ADATKINYERÉSEK › KÖTELEZŐ NYOMTATVÁNYOK (backend/nyomtatvanyok.js)
 *
 *  A 20/2012. (VIII. 31.) EMMI rendelet 94. §-a szerinti iskolai nyomtatványok, témák szerint
 *  csoportosítva. Ami a rendszer adataiból előállítható, az itt PDF-ben készül; ami máshol már
 *  megvan (bizonyítvány, törzslap, órarend, tantárgyfelosztás, KIR-STAT), arra hivatkozás mutat.
 *  Minden nyomtatványnál csak a hozzá tartozó mezők látszanak (lenyitva).
 *
 *  A PDF-et a kiszolgáló aláírja (aktív tanúsítvány, időbélyeg ha be van kapcsolva) és a
 *  dokumentumtárba teszi (tipus = 'nyomtatvany') – a felület ugyanúgy azonnal megnyitja.
 *
 *  LEFEDETTSÉG (2026-11-01): az oldal tetején a 94. § teljes nyomtatványlistája (a kiszolgáló
 *  regisztere, nyomtatvanyok.js KOTELEZO_94): itt készül / máshol készül / nem készül (miért). A
 *  sorra kattintva a nyomtatványhoz ugrik (lenyitja), vagy arra az oldalra visz, ahol készül.
 */

type MezoTipus = 'osztaly' | 'csoport' | 'tanulo' | 'tantargy' | 'datum' | 'honap' | 'valaszt' | 'szoveg';
/** `szuro`: csoportválasztónál csak a tanórai csoportok (csoportnapló), vagy csak az egyéb foglalkozások. */
/*  `alapNelkul`: a választónak nincs előre kiválasztott értéke (tudatosan kell választani).
 *  `kellHa`: csak akkor kötelező (és csak akkor látszik), ha egy másik mező értéke ez. */
interface Mezo { kulcs: string; cimke: string; tipus: MezoTipus; kotelezo?: boolean; tobb?: boolean; opciok?: { ertek: string; nev: string }[]; sugo?: string; szuro?: 'tanorai' | 'egyeb';
  alapNelkul?: boolean; kellHa?: { kulcs: string; ertek: string }; }
interface Nyomtatvany {
  id: string; cim: string; jogalap: string; leiras: string;
  /** `extra`: a kéréshez mindig hozzáadott paraméterek. */
  vegpont?: string; extra?: Record<string, string>; link?: { url: string; query?: any; cimke: string };
  /** A tárolt nyomtatvány fajtája (a regiszter `fajta`-ja), ha nem azonos a végponttal. */
  fajta?: string;
  mezok?: Mezo[];
}
interface Csoport { cim: string; ikon: string; elemek: Nyomtatvany[]; }
/** A 94. § egy tétele a kiszolgáló regiszteréből (nyomtatvanyok.js KOTELEZO_94). */
interface LefedettsegTetel {
  sorszam: number; megnevezes: string; allapot: 'generalt' | 'mashol' | 'nincs' | 'hatalyon_kivul';
  fajta?: string; nev?: string; tartalom?: string; hol?: { cim: string; url: string; query?: any };
  megjegyzes?: string; ok?: string; legutobb?: string; peldany?: number;
}
interface Lefedettseg {
  forras: { jogszabaly: string; url: string; idoallapot: string; olvasva: string };
  osszesito: Record<string, number>; tetelek: LefedettsegTetel[];
}

/** A papíralapú értesítő kiadásának oka – 20/2012. EMMI r. 74. § (1), 100. § (4) (backend: KIADAS_OKA). */
const KIADAS_OKA = [
  { ertek: 'szulo_kerese', nev: 'A szülő kérésére papíron (74. § (1))' },
  { ertek: 'iskola_dontese', nev: 'Papíron is, az iskola döntése alapján (74. § (1))' },
  { ertek: 'irasbeli_felevi', nev: 'Írásbeli félévi tájékoztatás, aláírással, bélyegzővel (100. § (4))' },
  { ertek: 'egyeb', nev: 'Egyéb' },
];

const VIZSGA = [
  { ertek: 'osztalyozo', nev: 'Osztályozó vizsga' }, { ertek: 'javito', nev: 'Javítóvizsga' },
  { ertek: 'kulonbozeti', nev: 'Különbözeti vizsga' }, { ertek: 'potlo', nev: 'Pótló vizsga' },
];

@Component({
  selector: 'app-kotelezo-nyomtatvanyok',
  templateUrl: './kotelezo-nyomtatvanyok.component.html',
  styleUrls: ['./kotelezo-nyomtatvanyok.component.scss'],
})
export class KotelezoNyomtatvanyokComponent implements OnInit {
  @Input() jogosult = false;

  intezmeny = '';
  osztalyok: { id: string; nev: string }[] = [];
  /** A csoport foglalkozás-típusa (backend/csoport-nevsor.js): tanórai csoport → csoportnapló, a többi
   *  (szakkör, sportkör, napközi, tanulószoba, egyéb foglalkozás) → egyéb foglalkozási napló. */
  csoportok: { id: string; nev: string; egyeb: boolean; tipus?: string; tipus_nev?: string }[] = [];
  tanoraiCsoportok: { id: string; nev: string; egyeb: boolean; tipus?: string; tipus_nev?: string }[] = [];
  egyebCsoportok: { id: string; nev: string; egyeb: boolean; tipus?: string; tipus_nev?: string }[] = [];
  tanev: any = null;
  /** osztály id → { tanulok, tantargyak } */
  private osztalyAdat = new Map<string, { tanulok: { oktazon: string; nev: string }[]; tantargyak: string[] }>();
  ertek: Record<string, Record<string, any>> = {};
  fut: Record<string, boolean> = {};
  /** A 94. § lefedettségi regisztere (null: még tölt, vagy nem érhető el). */
  lefedettseg: Lefedettseg | null = null;
  lefedettsegHiba = '';
  /** Melyik nyomtatvány van lenyitva / kiemelve (a lefedettségi táblából ugorva). */
  nyitva: Record<string, string | undefined> = {};
  kiemelt = '';

  readonly csoportLista: Csoport[] = [
    { cim: 'Tanulói nyilvántartások', ikon: 'people-outline', elemek: [
      { id: 'beirasi', cim: 'Beírási napló', jogalap: '95. §', vegpont: 'beirasi-naplo',
        leiras: 'Az iskolába felvett összes tanuló a beírási napló sorszáma szerint: felvétel, személyes adatok, igények, megszűnés (ideje, oka, az új intézmény), évfolyamismétlés, SNI/BTMN a szakértői vélemény adataival.' },
      { id: 'vegzett', cim: 'Végzett tanulók nyilvántartása', jogalap: '106. §', vegpont: 'vegzett-tanulok',
        leiras: 'A tanévben megszűnt jogviszonyok és a legfelső évfolyam tanulói; az átvevő iskola adatainak helyével.' },
      { id: 'igazolas', cim: 'Tanulói jogviszony igazolása', jogalap: '94. § 24.', vegpont: 'jogviszony-igazolas',
        leiras: 'Igazolás a tanulói jogviszonyról – egy tanulónak, vagy az osztály minden tanulójának.',
        mezok: [{ kulcs: 'osztaly', cimke: 'Osztály', tipus: 'osztaly', kotelezo: true }, { kulcs: 'tanulo', cimke: 'Tanuló (üresen: az egész osztály)', tipus: 'tanulo' },
          { kulcs: 'cel', cimke: 'Az igazolás célja (nem kötelező)', tipus: 'szoveg', sugo: 'pl. családi pótlék' }, { kulcs: 'datum', cimke: 'Kelt', tipus: 'datum' }] },
      { id: 'iksz', cim: 'Közösségi szolgálati jelentkezési lap', jogalap: '133. § (9)', vegpont: 'kozossegi-szolgalat', fajta: 'kozossegi-szolgalat-jelentkezes',
        leiras: 'Előre kitöltött jelentkezési lap a szülő egyetértő nyilatkozatával (középiskola).',
        mezok: [{ kulcs: 'osztaly', cimke: 'Osztály', tipus: 'osztaly', kotelezo: true }, { kulcs: 'tanulo', cimke: 'Tanuló (üresen: az egész osztály)', tipus: 'tanulo' }] },
    ] },
    { cim: 'Naplók', ikon: 'book-outline', elemek: [
      { id: 'osztalynaplo', cim: 'Osztálynapló', jogalap: '101. §', vegpont: 'osztalynaplo',
        leiras: 'Haladási rész (az órák anyaga, tartója, beírója, hiányzói), mulasztási rész (napi kimutatás, heti, féléves és éves összesítés) és értékelő rész (havi bontás, félév, év vége).',
        mezok: [{ kulcs: 'osztaly', cimke: 'Osztály', tipus: 'osztaly', kotelezo: true },
          { kulcs: 'resz', cimke: 'Rész', tipus: 'valaszt', opciok: [{ ertek: 'mind', nev: 'Mindhárom rész' }, { ertek: 'haladasi', nev: 'Haladási' }, { ertek: 'mulasztasi', nev: 'Mulasztási' }, { ertek: 'ertekelo', nev: 'Értékelő' }] },
          { kulcs: 'tol', cimke: 'Időszak kezdete', tipus: 'datum' }, { kulcs: 'ig', cimke: 'Időszak vége', tipus: 'datum' }] },
      { id: 'csoportnaplo', cim: 'Csoportnapló', jogalap: '102. §', vegpont: 'csoportnaplo', extra: { fajta: 'csoport' },
        leiras: 'Csoportbontással kialakított tanórai foglalkozás: névsor osztállyal, a tanulók osztályonkénti megoszlása, hiányzások, a foglalkozások sorszáma, időpontja és tananyaga, értékelés.',
        mezok: [{ kulcs: 'csoport', cimke: 'Csoport (tanórai)', tipus: 'csoport', kotelezo: true, szuro: 'tanorai' }, { kulcs: 'tol', cimke: 'Időszak kezdete', tipus: 'datum' }, { kulcs: 'ig', cimke: 'Időszak vége', tipus: 'datum' }] },
      { id: 'egyebnaplo', cim: 'Egyéb foglalkozási napló', jogalap: '103. §', vegpont: 'egyeb-foglalkozasi-naplo',
        leiras: 'Szakkör, sportkör, napközi, tanulószoba és más egyéb foglalkozás: a foglalkozás helye, ideje, időtartama, névsor osztállyal, az anya és a törvényes képviselő elérhetősége, a foglalkozások sorszáma, időpontja, témája, és jelenléti kimutatás. A csoport fajtája a Csoportbesorolásnál állítható.',
        mezok: [{ kulcs: 'csoport', cimke: 'Foglalkozás (csoport)', tipus: 'csoport', kotelezo: true, szuro: 'egyeb' }, { kulcs: 'tol', cimke: 'Időszak kezdete', tipus: 'datum' }, { kulcs: 'ig', cimke: 'Időszak vége', tipus: 'datum' }] },
    ] },
    { cim: 'Értékelés, bizonyítvány', ikon: 'ribbon-outline', elemek: [
      { id: 'ertesito', cim: 'Értesítő (félévi, papíron)', jogalap: '100. §, 74. § (1)', vegpont: 'ertesito',
        leiras: 'A félévi osztályzatról a szülőt a tanulmányi rendszeren át kell értesíteni; papíron emellett lehet, a szülő kérésére kell (74. § (1)). Ezért a kiadás okát meg kell adni – a lapra és a kiadott irat nyilvántartásába (Generált dokumentumok) is bekerül. Tanulónként: adatok, szülői elérhetőség, magatartás és szorgalom, tantárgyi értékelések, félévi osztályzatok, a mulasztások igazolása; aláírás és bélyegző helyével.',
        mezok: [{ kulcs: 'osztaly', cimke: 'Osztály', tipus: 'osztaly', kotelezo: true }, { kulcs: 'tanulo', cimke: 'Tanuló(k) (üresen: az egész osztály)', tipus: 'tanulo', tobb: true },
          { kulcs: 'kiadas_oka', cimke: 'A papíron kiadás oka', tipus: 'valaszt', kotelezo: true, alapNelkul: true, opciok: KIADAS_OKA },
          { kulcs: 'kiadas_megjegyzes', cimke: 'Az egyéb ok leírása', tipus: 'szoveg', sugo: 'pl. a szülő nem fér hozzá a tanulmányi rendszerhez', kellHa: { kulcs: 'kiadas_oka', ertek: 'egyeb' } },
          { kulcs: 'datum', cimke: 'Állapot napja (alapból a félév vége)', tipus: 'datum' }] },
      { id: 'bizonyitvany', cim: 'Bizonyítvány, törzslap', jogalap: '96., 99. §', link: { url: '/admin/dokumentumok', query: { nezet: 'generalas' }, cimke: 'Záróértékelés dokumentumok' },
        leiras: 'A bizonyítvány (aláírással, tárolva) és a törzslap a Záróértékelés dokumentumok oldalon készül.' },
    ] },
    { cim: 'Tanulmányok alatti vizsgák', ikon: 'create-outline', elemek: [
      { id: 'vizsga', cim: 'Vizsgajegyzőkönyv', jogalap: '104. §', vegpont: 'vizsga-jegyzokonyv',
        leiras: 'Tanulónként és vizsgatárgyanként, a tanuló adataival előre kitöltve; az írásbeli és szóbeli rész, a kérdések és a bizottság aláírása helyével.',
        mezok: [{ kulcs: 'osztaly', cimke: 'Osztály', tipus: 'osztaly', kotelezo: true }, { kulcs: 'tanulo', cimke: 'Tanuló', tipus: 'tanulo', kotelezo: true },
          { kulcs: 'tantargy', cimke: 'Vizsgatárgy(ak)', tipus: 'tantargy', tobb: true }, { kulcs: 'vizsga', cimke: 'A vizsga fajtája', tipus: 'valaszt', opciok: VIZSGA },
          { kulcs: 'datum', cimke: 'A vizsga napja (nem kötelező)', tipus: 'datum' }] },
      { id: 'osztalyozoiv', cim: 'Osztályozóív', jogalap: '94. § 15.', vegpont: 'osztalyozoiv',
        leiras: 'A vizsgázók névsora az írásbeli, szóbeli, gyakorlati és végleges osztályzat rovataival.',
        mezok: [{ kulcs: 'osztaly', cimke: 'Osztály', tipus: 'osztaly', kotelezo: true }, { kulcs: 'tantargy', cimke: 'Vizsgatárgy', tipus: 'tantargy' },
          { kulcs: 'tanulok', cimke: 'Vizsgázók (üresen: az egész osztály)', tipus: 'tanulo', tobb: true }, { kulcs: 'vizsga', cimke: 'A vizsga fajtája', tipus: 'valaszt', opciok: VIZSGA },
          { kulcs: 'datum', cimke: 'A vizsga napja (nem kötelező)', tipus: 'datum' }] },
    ] },
    { cim: 'Mulasztás', ikon: 'alert-circle-outline', elemek: [
      { id: 'mulasztas', cim: 'Értesítés igazolatlan mulasztásról', jogalap: '51. § (3)–(5)', vegpont: 'mulasztas-ertesito',
        leiras: 'A küszöböt (1, 10, 30, 50 igazolatlan óra) elért tanulók listája tanulónként a rendelet szerinti értesítendőkkel (tanköteles / nem tanköteles, kiskorú), és a szülőnek szóló levelek. A késésekből adódó órák is számítanak. Az „Értesítve” oszlop az Osztályfőnök oldalon rögzített értesítésekből töltődik.',
        mezok: [{ kulcs: 'kuszob', cimke: 'Küszöb', tipus: 'valaszt', kotelezo: true, opciok: [{ ertek: '1', nev: 'Első igazolatlan óra' }, { ertek: '10', nev: '10 óra' }, { ertek: '30', nev: '30 óra' }, { ertek: '50', nev: '50 óra' }] },
          { kulcs: 'osztaly', cimke: 'Osztály (üresen: az egész intézmény)', tipus: 'osztaly' }] },
      { id: 'figyelmeztetes', cim: 'Írásbeli figyelmeztetés (nem tanköteles tanuló)', jogalap: '50. § (4)', vegpont: 'igazolatlan-figyelmeztetes',
        leiras: 'A nem tanköteles tanuló jogviszonya 30 igazolatlan óránál több után megszűnik, ha az iskola előtte legalább kétszer írásban figyelmeztette (kiskorúnál a szülőt). Az 1. és a 2. figyelmeztetés levele, átvételi elismervénnyel; a kiadás tényét az Osztályfőnök oldalon kell rögzíteni.',
        mezok: [{ kulcs: 'osztaly', cimke: 'Osztály', tipus: 'osztaly', kotelezo: true }, { kulcs: 'tanulo', cimke: 'Tanuló', tipus: 'tanulo', kotelezo: true },
          { kulcs: 'sorszam', cimke: 'Figyelmeztetés', tipus: 'valaszt', kotelezo: true, opciok: [{ ertek: '1', nev: '1. figyelmeztetés' }, { ertek: '2', nev: '2. figyelmeztetés' }] }] },
    ] },
    { cim: 'Tanévszervezés, ellátás, adatszolgáltatás', ikon: 'calendar-outline', elemek: [
      { id: 'etkezes', cim: 'Étkeztetési nyilvántartás', jogalap: '94. § 22.', vegpont: 'etkezes', fajta: 'etkeztetesi-nyilvantartas',
        leiras: 'Havi kimutatás osztályonként: napi rendelések, összesen, kedvezmény, diéta.',
        mezok: [{ kulcs: 'honap', cimke: 'Hónap', tipus: 'honap', kotelezo: true }, { kulcs: 'osztaly', cimke: 'Osztály (üresen: mind)', tipus: 'osztaly' }] },
      { id: 'orarend', cim: 'Órarend', jogalap: '94. § 17.', link: { url: '/orarend', cimke: 'Órarend › Nyomtatás' },
        leiras: 'Osztály, tanár, terem vagy tantárgy szerinti nyomtatás az Órarend oldalon.' },
      { id: 'ttf', cim: 'Tantárgyfelosztás', jogalap: '105. §', link: { url: '/ttf', cimke: 'Tantárgyfelosztás' },
        leiras: 'Tanáronkénti és teljes (A3) nyomtatás a Tantárgyfelosztás oldalon; a fenntartónak augusztus 15-ig.' },
      { id: 'kirstat', cim: 'KIR-STAT segédlet (október 1.)', jogalap: '229/2012. Korm. r. 25. §', link: { url: '/admin/kirstat', cimke: 'KIR-STAT segédlet' },
        leiras: 'Az októberi statisztika kitöltéséhez a rendszer adataiból számolt táblák.' },
    ] },
  ];

  constructor(private http: HttpClient, private data: DataService, private router: Router) {}

  async ngOnInit() {
    const si: any = await this.data.getFromStorage('sajatIntezmeny');
    this.intezmeny = String(si?.intezmenyId || '').replace(/^\/+|\/+$/g, '');
    for (const cs of this.csoportLista) for (const n of cs.elemek) {
      this.ertek[n.id] = {};
      for (const m of n.mezok || []) if (m.tipus === 'valaszt' && m.opciok?.length && m.kotelezo !== false && !m.alapNelkul) this.ertek[n.id][m.kulcs] = m.opciok[0].ertek;
    }
    this.ertek['etkezes']['honap'] = new Date().toISOString().slice(0, 7);
    this.ertek['mulasztas']['kuszob'] = '1';
    if (!this.intezmeny) return;
    this.lefedettsegBetolt();
    try {
      const v: any = await firstValueFrom(this.http.get(`/api/nyomtatvany/valasztek/${this.intezmeny}`));
      this.osztalyok = v?.osztalyok || [];
      this.csoportok = v?.csoportok || [];
      this.tanoraiCsoportok = this.csoportok.filter((c) => !c.egyeb);
      this.egyebCsoportok = this.csoportok.filter((c) => c.egyeb);
      this.tanev = v?.tanev || null;
    } catch (e: any) {
      if (this.jogosult) this.data.sendToast(e?.error?.error || 'A nyomtatványok választólistái nem töltődtek be.', 'error');
    }
  }

  tanulok(id: string) { return this.osztalyAdat.get(this.ertek[id]?.['osztaly'])?.tanulok || []; }
  tantargyak(id: string) { return this.osztalyAdat.get(this.ertek[id]?.['osztaly'])?.tantargyak || []; }

  async osztalyValt(id: string) {
    const o = this.ertek[id]?.['osztaly'];
    for (const k of ['tanulo', 'tanulok', 'tantargy']) delete this.ertek[id][k];
    if (!o || this.osztalyAdat.has(o)) return;
    const n = this.csoportLista.flatMap((c) => c.elemek).find((x) => x.id === id);
    if (!n?.mezok?.some((m) => m.tipus === 'tanulo' || m.tipus === 'tantargy')) return;
    try {
      const v: any = await firstValueFrom(this.http.get(`/api/nyomtatvany/valasztek/${this.intezmeny}?osztaly=${encodeURIComponent(o)}`));
      this.osztalyAdat.set(o, { tanulok: v?.tanulok || [], tantargyak: v?.tantargyak || [] });
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Az osztály adatai nem töltődtek be.', 'error');
    }
  }

  hianyzik(n: Nyomtatvany): string {
    const m = (n.mezok || []).find((x) => (x.kotelezo || (x.kellHa && this.latszik(n, x))) && !this.ertekVan(this.ertek[n.id]?.[x.kulcs]));
    return m ? m.cimke : '';
  }
  /** A feltételes mező (`kellHa`) csak akkor látszik, ha a másik mező értéke a megadott. */
  latszik(n: Nyomtatvany, m: Mezo): boolean {
    return !m.kellHa || this.ertek[n.id]?.[m.kellHa.kulcs] === m.kellHa.ertek;
  }

  /* ─────────── 94. § lefedettség */

  private async lefedettsegBetolt() {
    try {
      this.lefedettseg = await firstValueFrom(this.http.get<Lefedettseg>(`/api/nyomtatvany/lefedettseg/${this.intezmeny}`));
      this.lefedettsegHiba = '';
    } catch (e: any) {
      this.lefedettseg = null;
      this.lefedettsegHiba = e?.error?.error || 'A lefedettségi összesítő nem töltődött be.';
    }
  }

  readonly ALLAPOT_NEV: Record<string, string> = { generalt: 'Itt készül', mashol: 'Máshol készül', nincs: 'Nem készül', hatalyon_kivul: 'Hatályon kívül' };

  /** A regiszter tételéhez tartozó nyomtatvány ezen az oldalon (fajta vagy végpont szerint). */
  private helyiNyomtatvany(x: LefedettsegTetel): Nyomtatvany | undefined {
    if (!x.fajta) return undefined;
    return this.csoportLista.flatMap((c) => c.elemek).find((n) => (n.fajta || n.vegpont) === x.fajta);
  }
  hova(x: LefedettsegTetel): string {
    if (x.allapot === 'generalt') return this.helyiNyomtatvany(x)?.cim || x.nev || '';
    if (x.allapot === 'mashol') return x.hol?.cim || '';
    return '';
  }
  kattinthato(x: LefedettsegTetel): boolean {
    return (x.allapot === 'generalt' && !!this.helyiNyomtatvany(x)) || (x.allapot === 'mashol' && !!x.hol?.url);
  }

  /** Ugrás: az itt készülőhöz lenyitja és odagörget, a máshol készülőnél arra az oldalra visz. */
  ugras(x: LefedettsegTetel) {
    if (x.allapot === 'mashol' && x.hol?.url) { this.router.navigate([x.hol.url], { queryParams: x.hol.query || {} }); return; }
    const n = this.helyiNyomtatvany(x);
    if (!n) return;
    if (n.mezok?.length) this.nyitva[n.id] = n.id;
    this.kiemelt = n.id;
    setTimeout(() => document.getElementById(`nyomtatvany-${n.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
    setTimeout(() => { if (this.kiemelt === n.id) this.kiemelt = ''; }, 2500);
  }
  /** Az accordion-csoport saját váltása (a benne lévő ion-select ionChange-e is felbuborékol – azt nem). */
  nyitasValt(n: Nyomtatvany, ev: any) {
    if (ev?.target?.tagName !== 'ION-ACCORDION-GROUP') return;
    this.nyitva[n.id] = ev?.detail?.value || undefined;
  }
  private ertekVan(v: any) { return Array.isArray(v) ? v.length > 0 : v !== undefined && v !== null && String(v).trim() !== ''; }

  async keszit(n: Nyomtatvany) {
    if (n.link) { this.router.navigate([n.link.url], { queryParams: n.link.query || {} }); return; }
    if (!this.jogosult) { this.data.sendToast('Ehhez admin jogosultság szükséges.', 'warning'); return; }
    const hiany = this.hianyzik(n);
    if (hiany) { this.data.sendToast(`Válaszd ki: ${hiany}.`, 'warning'); return; }
    const p = new URLSearchParams(n.extra || {});
    for (const [k, v] of Object.entries(this.ertek[n.id] || {})) {
      if (!this.ertekVan(v)) continue;
      const mezo = n.mezok?.find((m) => m.kulcs === k);
      if (mezo && !this.latszik(n, mezo)) continue;
      p.set(k, Array.isArray(v) ? v.join(',') : String(v));
    }
    if (this.tanev?.ev && !p.has('tanev')) p.set('tanev', String(this.tanev.ev));
    this.fut[n.id] = true;
    try {
      await this.data.megnyitFajl(`/api/nyomtatvany/${n.vegpont}/${this.intezmeny}?${p.toString()}`, n.cim);
    } finally {
      this.fut[n.id] = false;
      //  Az összesítő „legutóbb” oszlopa frissüljön.
      this.lefedettsegBetolt();
    }
  }
}
